import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, FilterSelection, GroupWrapper } from '../Filters.style';
import { FilterOption, FilterResultCounts, RangeResultsCounts } from '../../../types';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import FilterExposedHeader from '../FilterExposedHeader';
import {
  ShowAllButton,
  ModelFamilyStyleFrame,
  AllModelsWrapper,
  Legend,
  CTAWrapper,
  CommonModelsTitle,
} from './ModelFamilySelector.style';
import CommonModelsList from './CommonModels';
import Search from './Search/Search';
import ActiveModelFamiliesList from './ActiveModelFamilies';
import CheckboxSelector from '../../CheckboxComponent';
import useAnimations from '../../../hooks/Animations/useAnimations';
import { AvailableFiltersCount } from '../../../types/oneGraphTypes';

interface ModelFamilySelectorTypes {
  filterType: string;
  isTier3page: boolean;
  modelFamilyOptions: {
    [key: string]: AvailableFiltersCount;
  };
  confirmedFilters: FilterTypes;
  openedFiltersList: ExposedFiltersTypes;
  commonModelFamilies: string[];
  handleAddFilter: (filterType: string, value: string) => void;
  handleExposeFilter: (arg: string) => void;
}

const ModelFamilySelector: React.FC<ModelFamilySelectorTypes> = ({
  filterType,
  modelFamilyOptions,
  confirmedFilters,
  isTier3page,
  commonModelFamilies,
  handleAddFilter,
  handleExposeFilter,
  openedFiltersList,
}) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  // Animation
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const selectedModelFamilyFilters = confirmedFilters.modelFamily;
  const isNew = confirmedFilters.vehicleType === 'new';

  const commmonModelFamilyOptions = useMemo(() => {
    // Convert array to Set
    const convertedToSet = new Set(commonModelFamilies);

    // Filter out wrong values
    const filtered = Object.fromEntries(
      Object.entries(modelFamilyOptions).filter(([key, value]) => convertedToSet.has(value.value)),
    );

    // Sorting the object, so it is following the initial order from array
    const sorted = Object.fromEntries(
      Object.entries(filtered).sort(([keyA], [keyB]) => {
        return commonModelFamilies.indexOf(keyA) - commonModelFamilies.indexOf(keyB);
      }),
    );

    return sorted;
  }, [commonModelFamilies, modelFamilyOptions]);

  const groupedModelFamilies = useMemo(() => {
    // Filter options with defined tabs
    const allOptions = Object.values(modelFamilyOptions);

    // Hide filter options that shouldn't be on the screen
    const filteredOptions = allOptions.filter((option) => !option.isHidden);

    // Group options by tabs using reduce
    const groupedByTabs = filteredOptions.reduce(
      (acc, item) => {
        item.tabs?.forEach((tab) => {
          acc[tab] = acc[tab] || [];
          acc[tab].push(item);
        });
        return acc;
      },
      {} as { [key: string]: AvailableFiltersCount[] },
    );

    return groupedByTabs;
  }, [modelFamilyOptions]);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [showAll, groupedModelFamilies]);

  const handleAddModelFamily = (newModelFamily: string) => {
    handleAddFilter('modelFamily', newModelFamily);
  };

  const showFilter = openedFiltersList[filterType];

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(showFilter);

  return (
    <Filter>
      <FilterExposedHeader
        filterType={filterType}
        selectedFilterLabels={selectedModelFamilyFilters}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      {dropdownAnimation(
        (props, item) =>
          item && (
            <FilterSelection style={props} data-testid="model-family-filter-group">
              <ModelFamilyStyleFrame style={filtersAccordianAnimation}>
                <Search
                  allOptions={Object.keys(modelFamilyOptions)}
                  modelFamilyOptions={modelFamilyOptions}
                  onChange={handleAddModelFamily}
                />
                <ActiveModelFamiliesList
                  modelFamilyOptions={modelFamilyOptions}
                  selectedModelFamilyFilters={selectedModelFamilyFilters}
                  filterType={filterType}
                  onChange={handleAddModelFamily}
                />
                {/* Don't show common models section for Tier 3 pre-owned */}
                {!isNew && isTier3page ? (
                  <></>
                ) : (
                  <>
                    <CommonModelsTitle>{t(`vlp.filters.models.modelsNote`)}</CommonModelsTitle>
                    <CommonModelsList
                      commonModelFamilyOptions={commmonModelFamilyOptions}
                      selectedModelFamilyFilters={selectedModelFamilyFilters}
                      filterType={filterType}
                      onChange={handleAddModelFamily}
                    />
                  </>
                )}
                <CTAWrapper expanded={showAll}>
                  <ShowAllButton
                    onClick={() => setShowAll(!showAll)}
                    data-testid="show-all-filters"
                  >
                    {showAll ? t(`vlp.filters.models.showFewer`) : t(`vlp.filters.models.showAll`)}
                  </ShowAllButton>
                </CTAWrapper>
                <AllModelsWrapper
                  expanded={showAll}
                  ref={contentRef}
                  contentHeight={`${contentHeight}px` || '0'}
                >
                  {Object.keys(groupedModelFamilies).map((group) => {
                    return (
                      <div key={group}>
                        <Legend>{t(`vlp.filters.models.listOfModels.group.${group}`)}</Legend>
                        <GroupWrapper>
                          {groupedModelFamilies[group].map((model) => {
                            const { value, disabled } = model;
                            return (
                              <CheckboxSelector
                                filterType={filterType}
                                inputName={`${filterType}-${value}`}
                                key={value}
                                onChange={() => handleAddFilter(filterType, value)}
                                value={value}
                                checked={selectedModelFamilyFilters.includes(value)}
                                disabled={disabled}
                              />
                            );
                          })}
                        </GroupWrapper>
                      </div>
                    );
                  })}
                </AllModelsWrapper>
              </ModelFamilyStyleFrame>
            </FilterSelection>
          ),
      )}
    </Filter>
  );
};

export default ModelFamilySelector;
